import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Box from '@amzn/awsui-components-core/box';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import StatusBadge from 'src/components/atoms/StatusBadge';
import { CampaignData } from 'src/@types/valueMatching';
import { useTranslate } from 'src/hooks/useTranslate';

export interface CampaignStatusSectionProps {
    /** Full campaign data object */
    campaignData: CampaignData;
}

/**
 * Campaign status section — displays campaign ID, version, and status badge.
 * Shared between View and Modify pages.
 * Owns its own i18n bundle (`organisms.campaignStatusSection`).
 */
const CampaignStatusSection: FC<CampaignStatusSectionProps> = ({ campaignData }) => {
    const { translate } = useTranslate('organisms.campaignStatusSection');

    return (
        <Container header={<Header variant="h2">{translate('section_title')}</Header>}>
            <ColumnLayout columns={3}>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('campaign_id_label')}</Box>
                    <Box data-testid="campaign-id">{campaignData.campaignId ?? '—'}</Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('version_label')}</Box>
                    <Box data-testid="campaign-version">
                        {campaignData.version != null ? String(campaignData.version) : '—'}
                    </Box>
                </SpaceBetween>
                <SpaceBetween size="xs">
                    <Box variant="h4">{translate('status_label')}</Box>
                    <Box data-testid="campaign-status">
                        {campaignData.campaignStatus ? (
                            <StatusBadge status={campaignData.campaignStatus} />
                        ) : (
                            '—'
                        )}
                    </Box>
                </SpaceBetween>
            </ColumnLayout>
        </Container>
    );
};

export default CampaignStatusSection;
